import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PawPrint, Camera } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Guest pet photo strip for the pet-friendly landing pages. Shows
 * admin-approved guest photos (newest first) plus an always-present upload
 * invite. Submissions are moderated at /admin/pet-photos before appearing here.
 */
export const PetPhotoStrip = () => {
  const [photos, setPhotos] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/pet-photos`)
      .then((r) => r.json())
      .then((d) => {
        setFeatured(d.featured || null);
        setPhotos(d.photos || []);
      })
      .catch(() => {});
  }, []);

  const caption = (p) => (
    <figcaption className="mt-2 text-sm text-emerald-soft font-light leading-snug">
      {p.comment ? <span>"{p.comment}" — </span> : null}
      <span className="font-medium text-emerald">{p.pet_names}</span>
      <span className="text-emerald-soft/70"> · shared by {p.uploader_name}</span>
    </figcaption>
  );

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 mt-16" data-testid="pet-photo-strip">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <h2 className="font-display text-2xl md:text-3xl tracking-tight flex items-center gap-3">
          <PawPrint size={22} weight="duotone" className="text-sunset" />
          Our four-legged guests
        </h2>
        <Link
          to="/pet-photo-upload"
          data-testid="pet-photo-upload-link"
          className="text-sm font-medium text-sunset hover:text-emerald transition-colors duration-300"
        >
          Share your pet's photo →
        </Link>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-3 -mx-1 px-1 pt-4">
        {featured && (
          <figure key={featured.id} className="w-72 shrink-0 relative" data-testid={`pet-photo-featured-${featured.id}`}>
            <span
              data-testid="pet-of-the-week-badge"
              className="absolute -top-3 left-4 z-10 bg-sunset text-cream text-[11px] font-semibold uppercase tracking-[0.14em] px-3 py-1 rounded-full shadow-sm"
            >
              Pet of the week
            </span>
            <img
              src={`${API}${featured.image_url}`}
              alt={`${featured.pet_names}, Pet of the Week at Double Rainbow Suites`}
              loading="lazy"
              className="w-72 h-72 object-cover rounded-2xl border-2 border-sunset/40"
            />
            {caption(featured)}
          </figure>
        )}
        {photos.map((p) => (
          <figure key={p.id} className="w-56 shrink-0" data-testid={`pet-photo-${p.id}`}>
            <img
              src={`${API}${p.image_url}`}
              alt={`${p.pet_names}, a pet guest at Double Rainbow Suites`}
              loading="lazy"
              className="w-56 h-56 object-cover rounded-2xl border border-emerald/10"
            />
            {caption(p)}
          </figure>
        ))}
        <Link
          to="/pet-photo-upload"
          data-testid="pet-photo-upload-tile"
          className="w-56 h-56 shrink-0 rounded-2xl border-2 border-dashed border-emerald/20 grid place-items-center text-center hover:border-sunset/60 transition-colors duration-300"
        >
          <div className="px-4">
            <Camera size={26} weight="duotone" className="text-sunset mx-auto mb-2" />
            <p className="text-sm text-emerald font-medium">Add your pet</p>
            <p className="text-xs text-emerald-soft/70 mt-2.5 leading-relaxed">Reviewed by us before publishing.</p>
          </div>
        </Link>
      </div>
    </div>
  );
};
